import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from "react";

interface NavBarState {
  backAction: (() => void) | null;
  stepCurrent: number;
  stepTotal: number;
}

interface NavBarContextType extends NavBarState {
  setNavBar: (patch: Partial<NavBarState>) => void;
  clearNavBar: () => void;
}

const NavBarContext = createContext<NavBarContextType | null>(null);

const DEFAULT: NavBarState = { backAction: null, stepCurrent: 0, stepTotal: 0 };

export function NavBarProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<NavBarState>(DEFAULT);

  const setNavBar = useCallback((patch: Partial<NavBarState>) => {
    setState((s) => ({ ...s, ...patch }));
  }, []);

  const clearNavBar = useCallback(() => setState(DEFAULT), []);

  const value = useMemo(
    () => ({ ...state, setNavBar, clearNavBar }),
    [state, setNavBar, clearNavBar],
  );

  return (
    <NavBarContext.Provider value={value}>
      {children}
    </NavBarContext.Provider>
  );
}

export function useNavBar() {
  const ctx = useContext(NavBarContext);
  if (!ctx) throw new Error("useNavBar must be inside NavBarProvider");
  return ctx;
}
